import React from 'react';
import { Placement } from '@popperjs/core';
import {
    setVideoBackgroundBlurAmountAction,
    setVideoBackgroundImageAction,
    setVideoBackgroundModeAction,
} from 'flow/actions/video-actions';
import {
    selectVideoBackgroundEffectMode,
    selectVideoBackgroundImageUrl,
    selectVideoSenderVideoActive,
} from 'flow/selectors/video-selectors';
import { toggleSenderVideoActive } from 'flow/actions/meeting-actions';
import {
    setupLobbyVideoStream,
    stopLobbyVideoStream,
} from 'flow/actions/meeting-lobby-actions';
import {
    selectLobbyVideoActive,
    selectLobbyVideoDeviceInUse,
    selectMeetingIsActive,
} from 'flow/selectors/meeting-selectors';
import videoStreamProcessorManager from 'flow/utils/video-stream-processor-manager';
import {
    EFFECT_MODE,
    BLUR_AMOUNT,
} from 'flow/constants/video-background-constants';
import useAppContext from 'flow/hooks/useAppContext';
import useDarkMode from 'flow/hooks/useDarkMode';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { twMerge } from 'tailwind-merge';
import Icon from 'flow/components/ui-components/Icon';
import AnchoredPopover from './AnchoredPopover';
import CenteredDialog from './CenteredDialog';
import BlurDotsIcon from 'images/icon_blur_dots.svg';
import background1 from 'images/backgrounds/background-1.jpg';
import background2 from 'images/backgrounds/background-2.jpg';
import background3 from 'images/backgrounds/background-3.jpg';
import background4 from 'images/backgrounds/background-4.jpg';
import background5 from 'images/backgrounds/background-5.jpg';
import background6 from 'images/backgrounds/background-6.jpg';
import background7 from 'images/backgrounds/background-7.jpg';
import background8 from 'images/backgrounds/background-8.jpg';

interface BackgroundEffectsModalProps {
    onClose: () => void;
    /**
     * When provided, the effect picker renders as a popover anchored to this
     * element (no backdrop) and closes on click-away. Without it, it renders as
     * a centered modal dialog with a backdrop.
     */
    anchorEl?: HTMLElement | null;
    placement?: Placement;
    /**
     * Follow the app's light/dark theme (the lobby). Meeting surfaces leave this
     * off and stay always-dark to match the dark meeting UI.
     */
    followAppTheme?: boolean;
}

type EffectType = 'blur' | 'background' | 'upload';

interface EffectOption {
    id: string;
    type: EffectType;
    label: string;
    backgroundImage?: string;
    disabled?: boolean;
}

const BackgroundEffectsModal: React.FC<BackgroundEffectsModalProps> = ({
    onClose,
    anchorEl,
    placement = 'top',
    followAppTheme = false,
}) => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const isPopover = !!anchorEl;
    const darkModeIsActive = useDarkMode();
    // Meeting surfaces are always dark; the lobby follows the app theme.
    const dialogIsDark = followAppTheme ? darkModeIsActive : true;

    const effectMode = useAppSelector(selectVideoBackgroundEffectMode);
    const backgroundImageUrl = useAppSelector(selectVideoBackgroundImageUrl);
    const videoActive = useAppSelector(selectVideoSenderVideoActive);
    const isInActiveMeeting = useAppSelector(selectMeetingIsActive);
    const lobbyVideoActive = useAppSelector(selectLobbyVideoActive);
    const lobbyVideoDevice = useAppSelector(selectLobbyVideoDeviceInUse);

    const effectOptions: EffectOption[] = [
        {
            id: 'blur',
            type: 'blur',
            label: i18next.t('videoconference.background_effects.blur'),
        },
        {
            id: 'background-1',
            type: 'background',
            label: i18next.t('videoconference.background_effects.background', {
                number: 1,
            }),
            backgroundImage: background1,
        },
        {
            id: 'background-2',
            type: 'background',
            label: i18next.t('videoconference.background_effects.background', {
                number: 2,
            }),
            backgroundImage: background2,
        },
        {
            id: 'background-3',
            type: 'background',
            label: i18next.t('videoconference.background_effects.background', {
                number: 3,
            }),
            backgroundImage: background3,
        },
        {
            id: 'background-4',
            type: 'background',
            label: i18next.t('videoconference.background_effects.background', {
                number: 4,
            }),
            backgroundImage: background4,
        },
        {
            id: 'background-5',
            type: 'background',
            label: i18next.t('videoconference.background_effects.background', {
                number: 5,
            }),
            backgroundImage: background5,
        },
        {
            id: 'background-6',
            type: 'background',
            label: i18next.t('videoconference.background_effects.background', {
                number: 6,
            }),
            backgroundImage: background6,
        },
        {
            id: 'background-7',
            type: 'background',
            label: i18next.t('videoconference.background_effects.background', {
                number: 7,
            }),
            backgroundImage: background7,
        },
        {
            id: 'background-8',
            type: 'background',
            label: i18next.t('videoconference.background_effects.background', {
                number: 8,
            }),
            backgroundImage: background8,
        },
    ];

    const getCurrentSelection = (): string | null => {
        if (effectMode === EFFECT_MODE.BLUR) {
            return 'blur';
        }
        if (effectMode === EFFECT_MODE.IMAGE && backgroundImageUrl) {
            // Find which background matches the current URL
            const matchingOption = effectOptions.find(
                (opt) =>
                    opt.type === 'background' &&
                    opt.backgroundImage === backgroundImageUrl,
            );
            return matchingOption ? matchingOption.id : null;
        }
        return null;
    };

    const restartVideoStream = async () => {
        if (isInActiveMeeting && videoActive) {
            await dispatch(toggleSenderVideoActive());
            await dispatch(toggleSenderVideoActive());
        } else if (!isInActiveMeeting && lobbyVideoActive && lobbyVideoDevice) {
            await dispatch(stopLobbyVideoStream());
            await dispatch(setupLobbyVideoStream(lobbyVideoDevice.deviceId));
        }
    };

    const clearEffects = async () => {
        dispatch(setVideoBackgroundModeAction(EFFECT_MODE.NONE));
        dispatch(setVideoBackgroundImageAction(null));

        const processorActive = videoStreamProcessorManager.isActive();
        if (processorActive) {
            // Update settings on-the-fly if processor is already active (fast path)
            await videoStreamProcessorManager.updateEffectSettings(
                undefined,
                EFFECT_MODE.NONE,
                null,
            );
        }
    };

    const applyBlurEffect = async () => {
        dispatch(setVideoBackgroundBlurAmountAction(BLUR_AMOUNT.MEDIUM));
        dispatch(setVideoBackgroundModeAction(EFFECT_MODE.BLUR));
        dispatch(setVideoBackgroundImageAction(null));

        const processorActive = videoStreamProcessorManager.isActive();

        if (processorActive) {
            // Update settings on-the-fly if processor is already active (fast path)
            await videoStreamProcessorManager.updateEffectSettings(
                BLUR_AMOUNT.MEDIUM,
                EFFECT_MODE.BLUR,
                null,
            );
        } else {
            // Restart stream if no processor is active (slow path, full setup)
            await restartVideoStream();
        }
    };

    const applyBackgroundImage = async (imageUrl: string) => {
        dispatch(setVideoBackgroundModeAction(EFFECT_MODE.IMAGE));
        dispatch(setVideoBackgroundImageAction(imageUrl));

        const processorActive = videoStreamProcessorManager.isActive();

        if (processorActive) {
            // Update settings on-the-fly if processor is already active (fast path)
            await videoStreamProcessorManager.updateEffectSettings(
                undefined,
                EFFECT_MODE.IMAGE,
                imageUrl,
            );
        } else {
            // Restart stream if no processor is active (slow path, full setup)
            await restartVideoStream();
        }
    };

    const handleEffectSelect = async (
        option: EffectOption,
        event?: React.MouseEvent<HTMLButtonElement>,
    ) => {
        if (event) {
            event.currentTarget.blur();
        }

        if (option.disabled) {
            return;
        }

        try {
            const currentSelection = getCurrentSelection();
            const isClickingSameOption = currentSelection === option.id;

            if (isClickingSameOption) {
                await clearEffects();
                return;
            }

            if (option.type === 'blur') {
                await applyBlurEffect();
            } else if (option.type === 'background' && option.backgroundImage) {
                await applyBackgroundImage(option.backgroundImage);
            }
        } catch (error) {
            console.error('Error applying background effect:', error);
        }
    };

    const currentSelection = getCurrentSelection();

    // Non-selected tiles get a hover ring; its colour follows the panel theme.
    const hoverRingClass = dialogIsDark
        ? 'hover:ring-2 hover:ring-white/60'
        : 'hover:ring-2 hover:ring-black/20';

    // no-dark on the dark panel: it portals outside the meeting's no-dark
    // subtree, and the app inverts the gray scale under html.dark (gray-800 ->
    // light). no-dark pins the dark palette so a dark panel stays dark in every
    // theme (like MoreMenu's `no-dark !bg-gray-800`).
    const panelClassName = twMerge(
        'shadow-base flex flex-col gap-3 rounded-xl pb-6',
        dialogIsDark ? 'no-dark bg-gray-800 text-white' : 'bg-white text-black',
    );

    const panelBody = (
        <>
            {/* Header */}
            <div className="flex h-15 items-center justify-between ps-6 pe-3">
                <h2
                    id="background-effects-title"
                    className="text-xl font-semibold"
                >
                    {i18next.t('videoconference.background_effects.title')}
                </h2>
                <button
                    type="button"
                    onClick={onClose}
                    className={twMerge(
                        'focus-ring flex size-9 items-center justify-center rounded-full border-none bg-transparent transition-colors',
                        dialogIsDark
                            ? 'text-gray-300 hover:bg-white/10 hover:text-white'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                    )}
                    aria-label={i18next.t('close')}
                >
                    <Icon iconKey="close" size="1rem" />
                </button>
            </div>

            {/* Effect Options Grid */}
            <div className="grid grid-cols-3 gap-3 px-6">
                {effectOptions.map((option) => (
                    <button
                        key={option.id}
                        type="button"
                        onClick={(e) => handleEffectSelect(option, e)}
                        disabled={option.disabled}
                        className={twMerge(
                            'flex aspect-square cursor-pointer items-center justify-center rounded-lg transition-all',
                            'focus-ring',
                            currentSelection === option.id
                                ? 'ring-2 ring-blue-600'
                                : hoverRingClass,
                            option.disabled && 'cursor-not-allowed opacity-50',
                        )}
                    >
                        {option.type === 'blur' && (
                            <div
                                className={twMerge(
                                    'flex size-full items-center justify-center rounded-lg',
                                    dialogIsDark
                                        ? 'bg-gray-150'
                                        : 'bg-gray-300',
                                )}
                            >
                                <img
                                    src={BlurDotsIcon}
                                    alt="Blur"
                                    className="size-10"
                                />
                            </div>
                        )}
                        {option.type === 'background' &&
                            option.backgroundImage && (
                                <img
                                    src={option.backgroundImage}
                                    alt={option.label}
                                    className="size-full rounded-lg object-cover"
                                />
                            )}
                    </button>
                ))}
            </div>
        </>
    );

    // Anchored popover mode: renders next to the trigger with no backdrop and
    // closes on click-away.
    if (isPopover) {
        return (
            <AnchoredPopover
                anchorEl={anchorEl}
                onClose={onClose}
                placement={placement}
                role="dialog"
                aria-labelledby="background-effects-title"
                className={twMerge('w-99', panelClassName)}
            >
                {panelBody}
            </AnchoredPopover>
        );
    }

    return (
        <CenteredDialog
            onClose={onClose}
            aria-labelledby="background-effects-title"
            className={panelClassName}
        >
            {panelBody}
        </CenteredDialog>
    );
};

export default BackgroundEffectsModal;
